import serviciosV2Client from "../../../helpers/serviciosV2Client"

const ANALYTICS = "analytics/terceros/detallado"
const ANALYTICS_POR_SUCURSAL = "analytics/terceros/por-sucursal"
const EXPORT_BASE = "export/terceros-detallado"
const EXPORT_POR_SUCURSAL_BASE = "export/terceros-por-sucursal"
const CANONICOS = "terceros/canonicos"

const cleanParams = obj =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined),
  )

/**
 * Análisis detallado de terceros (cache 60s en el back).
 * Soporta dos modos: por year/mes o por fecha_inicio/fecha_fin.
 */
export const getTerceros = ({
  nitemisor, year, mes, fecha_inicio, fecha_fin,
  campo_fecha, sucursal, tipo_comprobante, excluir_consumidor_final,
  limit, offset,
}) =>
  serviciosV2Client.get(ANALYTICS, {
    params: cleanParams({
      nitemisor, year, mes, fecha_inicio, fecha_fin,
      campo_fecha, sucursal, tipo_comprobante,
      excluir_consumidor_final: excluir_consumidor_final ? true : undefined,
      limit, offset,
    }),
  })

/**
 * Descarga el export de terceros (CSV o XLSX) directo del back, en streaming.
 * Respeta Content-Disposition para el filename.
 */
export const descargarTercerosExport = async ({
  nitemisor, year, mes, fecha_inicio, fecha_fin,
  campo_fecha, sucursal, tipo_comprobante, excluir_consumidor_final,
  formato = "xlsx",
}) => {
  const params = cleanParams({
    nitemisor, year, mes, fecha_inicio, fecha_fin,
    campo_fecha, sucursal, tipo_comprobante,
    excluir_consumidor_final: excluir_consumidor_final ? true : undefined,
  })

  let response
  try {
    response = await serviciosV2Client.get(`${EXPORT_BASE}.${formato}`, {
      params,
      responseType: "blob",
      timeout: 600000,
    })
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text()
        const json = JSON.parse(text)
        throw new Error(json.error || json.message || "Error al exportar.")
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message.startsWith("Error al exportar")) throw parseErr
      }
    }
    throw err
  }

  const cd = response.headers["content-disposition"] || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  const filename = match
    ? decodeURIComponent(match[1])
    : `terceros_${nitemisor}_${new Date().toISOString().split("T")[0]}.${formato}`

  const contentType = response.headers["content-type"] ||
    (formato === "xlsx"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "text/csv;charset=utf-8;")

  const blob = new Blob([response.data], { type: contentType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return {
    totalRows: Number(response.headers["x-total-rows"]) || null,
    totalSheets: Number(response.headers["x-total-sheets"]) || null,
    filename,
  }
}

/* ────────────────────────────────────────────────────────────────────
 *  Terceros acumulados POR SUCURSAL (cache 60s en el back)
 * ──────────────────────────────────────────────────────────────────── */

/**
 * Análisis agrupado por sucursal: sucursales[] ordenadas por ventas DESC,
 * cada una con sus terceros (top-N).
 *
 * Modo fechas: pasar (fecha_inicio, fecha_fin) o (year, mes opcional).
 * `limit_por_sucursal` default 50, máx 1000.
 */
export const getTercerosPorSucursal = ({
  nitemisor, year, mes, fecha_inicio, fecha_fin,
  campo_fecha, sucursal, tipo_comprobante, excluir_consumidor_final,
  limit_por_sucursal,
}) =>
  serviciosV2Client.get(ANALYTICS_POR_SUCURSAL, {
    params: cleanParams({
      nitemisor, year, mes, fecha_inicio, fecha_fin,
      campo_fecha, sucursal, tipo_comprobante,
      excluir_consumidor_final: excluir_consumidor_final ? true : undefined,
      limit_por_sucursal,
    }),
  })

/**
 * Descarga XLSX con 1 hoja por sucursal + hoja Resumen.
 * Mismos filtros que el JSON pero sin `limit_por_sucursal`
 * (el Excel siempre incluye TODOS los terceros de cada sucursal).
 */
export const descargarTercerosPorSucursalExport = async ({
  nitemisor, year, mes, fecha_inicio, fecha_fin,
  campo_fecha, sucursal, tipo_comprobante, excluir_consumidor_final,
}) => {
  const params = cleanParams({
    nitemisor, year, mes, fecha_inicio, fecha_fin,
    campo_fecha, sucursal, tipo_comprobante,
    excluir_consumidor_final: excluir_consumidor_final ? true : undefined,
  })

  let response
  try {
    response = await serviciosV2Client.get(`${EXPORT_POR_SUCURSAL_BASE}.xlsx`, {
      params,
      responseType: "blob",
      timeout: 600000,
    })
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text()
        const json = JSON.parse(text)
        throw new Error(json.error || json.message || "Error al exportar.")
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message.startsWith("Error al exportar")) throw parseErr
      }
    }
    throw err
  }

  const cd = response.headers["content-disposition"] || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  const filename = match
    ? decodeURIComponent(match[1])
    : `terceros_por_sucursal_${nitemisor}_${new Date().toISOString().split("T")[0]}.xlsx`

  const contentType = response.headers["content-type"]
    || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

  const blob = new Blob([response.data], { type: contentType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return {
    totalRows: Number(response.headers["x-total-rows"]) || null,
    totalSheets: Number(response.headers["x-total-sheets"]) || null,
    filename,
  }
}

/* ────────────────────────────────────────────────────────────────────
 *  CRUD de overrides manuales del nombre canónico
 * ──────────────────────────────────────────────────────────────────── */

/** Lista todos los overrides del NIT emisor. */
export const listarOverridesCanonicos = nitemisor =>
  serviciosV2Client.get(CANONICOS, { params: { nitemisor } })

/** Devuelve el override de un receptor; 404 si no existe. */
export const getOverrideCanonico = (nitreceptor, nitemisor) =>
  serviciosV2Client.get(`${CANONICOS}/${encodeURIComponent(nitreceptor)}`, {
    params: { nitemisor },
  })

/**
 * Crea o actualiza un override (idempotente — el back usa
 * ReplacingMergeTree por timestamp).
 */
export const upsertOverrideCanonico = ({
  nitemisor, nitreceptor, nombre_canonico, tipo_documento = "31",
  actualizado_por, notas,
}) =>
  serviciosV2Client.post(CANONICOS, cleanParams({
    nitemisor, nitreceptor, nombre_canonico, tipo_documento,
    actualizado_por, notas,
  }))

/** Borrado lógico (preserva audit trail). */
export const deleteOverrideCanonico = ({ nitemisor, nitreceptor, actualizado_por }) =>
  serviciosV2Client.delete(`${CANONICOS}/${encodeURIComponent(nitreceptor)}`, {
    params: cleanParams({ nitemisor, actualizado_por }),
  })
